/**
 * Plaid Hosted Link connect for the native (Android/iOS) shells. Mirrors the brokered mobile sign-in
 * pattern (auth/mobileSignIn.ts): the OAuth round-trip can't complete in the Tauri WebView, so we open
 * Plaid's HOSTED Link URL in the SYSTEM BROWSER and then POLL the server until it reports the session
 * finished — no deep link, the return trip is a poll. The server exchanges the finished public_token(s)
 * during the poll, so a successful poll means the bank(s) are connected.
 *
 * The web build keeps the embedded Plaid Link (works in a real browser); this path is native-only.
 */
export interface HostedPlaidDeps {
  /** Mint a hosted link token + its browser URL (POST /me/financial/hosted-link-token). */
  createToken: () => Promise<{ linkToken: string; hostedLinkUrl: string }>;
  /** Poll for completion (POST /me/financial/hosted-complete); exchanges finished tokens server-side. */
  complete: (linkToken: string) => Promise<{ connected: number; pending: boolean; duplicates?: number }>;
  /** Open a URL in the system browser (Tauri opener). */
  openUrl: (url: string) => Promise<void>;
  /** Called once the browser is opened, with the link token — stash it so a manual "Finish" can retry. */
  onOpened?: (linkToken: string) => void;
  pollIntervalMs?: number;
  timeoutMs?: number;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  /** Subscribe to "app returned to the foreground" events; returns an unsubscribe fn. Injectable for
   *  tests. Defaults to the DOM `visibilitychange` listener. Mirrors auth/brokeredLink.ts. */
  onForeground?: (cb: () => void) => () => void;
}

/** Default foreground signal: fire `cb` whenever the document becomes visible again (the user switching
 *  back from the hosted-link browser). No-ops off the DOM (e.g. node test env). */
function domForeground(cb: () => void): () => void {
  if (typeof document === 'undefined') return () => {};
  const handler = (): void => {
    if (document.visibilityState === 'visible') cb();
  };
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
}

export interface HostedPlaidResult {
  connected: number;
  /** Institutions that were already connected (skipped as duplicates). */
  duplicates: number;
  timedOut: boolean;
  /** The link token — kept so the caller can auto-retry finishing the session after a timeout. */
  linkToken: string;
}

export async function hostedPlaidConnect(deps: HostedPlaidDeps): Promise<HostedPlaidResult> {
  const interval = deps.pollIntervalMs ?? 1200;
  const timeout = deps.timeoutMs ?? 300_000; // OAuth in a browser can take a while
  const now = deps.now ?? (() => Date.now());
  const sleep = deps.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const onForeground = deps.onForeground ?? domForeground;

  const { linkToken, hostedLinkUrl } = await deps.createToken();
  deps.onOpened?.(linkToken);
  await deps.openUrl(hostedLinkUrl);

  // Re-armable "app is foregrounded again" signal — race it against the interval sleep so the moment the
  // user returns from the hosted-link browser we poll IMMEDIATELY instead of waiting out the interval.
  // Android throttles/suspends background timers, so a blind sleep loop can stall while the app is
  // backgrounded; this makes the connect flip the instant the user is back. Mirrors auth/brokeredLink.ts.
  let wake!: () => void;
  let foregrounded = new Promise<void>((r) => { wake = r; });
  const detach = onForeground(() => {
    wake();
    foregrounded = new Promise<void>((r) => { wake = r; });
  });

  try {
    const deadline = now() + timeout;
    while (now() < deadline) {
      await Promise.race([sleep(interval), foregrounded]);
      try {
        const r = await deps.complete(linkToken);
        const duplicates = r.duplicates ?? 0;
        // Finish once the session resolves either way: something connected, or everything was a duplicate.
        if (r.connected > 0 || duplicates > 0) return { connected: r.connected, duplicates, timedOut: false, linkToken };
      } catch {
        /* transient — keep polling */
      }
    }
    return { connected: 0, duplicates: 0, timedOut: true, linkToken };
  } finally {
    detach();
  }
}
